// Financial-dashboard helpers.
//
// The gateway's `report=dashboard` returns monthly P&L rows plus AR/AP ageing
// totals. These helpers turn that payload into period totals, portfolio
// aggregates and a rule-based health score. All money figures are net of GST,
// matching the accounting reports they reconcile with.

import { api } from './api.js';
import type {
  DashboardAgingSummary,
  DashboardMonth,
  DashboardResult,
  DashboardStockSummary,
} from './api.js';

export type PeriodPreset =
  | 'this-month'
  | 'last-month'
  | 'this-quarter'
  | 'this-year'
  | 'last-6-months';

export interface PeriodOption {
  id: PeriodPreset;
  label: string;
}

export const PERIOD_OPTIONS: PeriodOption[] = [
  { id: 'this-month', label: 'This month' },
  { id: 'last-month', label: 'Last month' },
  { id: 'this-quarter', label: 'This quarter' },
  { id: 'this-year', label: 'This year' },
  { id: 'last-6-months', label: 'Last 6 months' },
];

export const DEFAULT_PERIOD: PeriodPreset = 'this-month';

export interface PeriodWindow {
  preset: PeriodPreset;
  label: string;
  /** Selected period, inclusive. */
  from: number;
  to: number;
  /** Immediately preceding period of the same shape (for deltas). */
  prevFrom: number;
  prevTo: number;
  /** Earliest month requested from the gateway (covers the 6-month chart). */
  chartFrom: number;
}

export interface PeriodTotals {
  sales: number;
  cogs: number;
  grossProfit: number;
  expenses: number;
  fees: number;
  netProfit: number;
}

export interface DashboardStoreInput {
  code: string;
  name: string;
  accessAs?: string[];
  renewalOnly?: boolean;
}

// ── dates ──────────────────────────────────────────────────────────────

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
}

function endOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
}

function startOfQuarter(d: Date): Date {
  return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1, 0, 0, 0, 0);
}

function endOfQuarter(d: Date): Date {
  const q = Math.floor(d.getMonth() / 3);
  return new Date(d.getFullYear(), q * 3 + 3, 0, 23, 59, 59, 999);
}

function addMonths(d: Date, months: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + months, d.getDate(), d.getHours(), d.getMinutes(), d.getSeconds(), d.getMilliseconds());
}

export function monthKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function monthKeysBetween(from: number, to: number): string[] {
  const out: string[] = [];
  let cursor = new Date(new Date(from).getFullYear(), new Date(from).getMonth(), 1);
  const end = new Date(new Date(to).getFullYear(), new Date(to).getMonth(), 1);
  while (cursor.getTime() <= end.getTime()) {
    out.push(monthKey(cursor.getTime()));
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return out;
}

/** Resolve a dashboard preset into concrete local-time ranges. */
export function resolvePeriod(preset: PeriodPreset, now = new Date()): PeriodWindow {
  const option = PERIOD_OPTIONS.find((p) => p.id === preset) || PERIOD_OPTIONS[0];
  let from: Date;
  let to: Date;
  let prevFrom: Date;
  let prevTo: Date;

  switch (preset) {
    case 'last-month': {
      const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      from = startOfMonth(prev);
      to = endOfMonth(prev);
      const before = new Date(now.getFullYear(), now.getMonth() - 2, 1);
      prevFrom = startOfMonth(before);
      prevTo = endOfMonth(before);
      break;
    }
    case 'this-quarter': {
      from = startOfQuarter(now);
      to = now;
      const q = Math.floor(now.getMonth() / 3);
      const before = new Date(now.getFullYear(), q * 3 - 3, 1);
      prevFrom = startOfQuarter(before);
      prevTo = endOfQuarter(before);
      break;
    }
    case 'this-year': {
      from = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      to = now;
      prevFrom = new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0);
      prevTo = new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999);
      break;
    }
    case 'last-6-months': {
      const start = new Date(now.getFullYear(), now.getMonth() - 5, 1, 0, 0, 0, 0);
      from = startOfMonth(start);
      to = now;
      const before = new Date(now.getFullYear(), now.getMonth() - 11, 1, 0, 0, 0, 0);
      prevFrom = startOfMonth(before);
      prevTo = endOfMonth(new Date(now.getFullYear(), now.getMonth() - 6, 1));
      break;
    }
    case 'this-month':
    default: {
      from = startOfMonth(now);
      to = now;
      const before = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      prevFrom = startOfMonth(before);
      prevTo = endOfMonth(before);
      break;
    }
  }

  // The chart always shows six months ending in the selected period, but a
  // longer period must still start at its own first month so its totals are
  // exact.
  const chartWindowStart = startOfMonth(addMonths(to, -5));
  const chartFrom = Math.min(chartWindowStart.getTime(), from.getTime());

  return {
    preset,
    label: option.label,
    from: from.getTime(),
    to: to.getTime(),
    prevFrom: prevFrom.getTime(),
    prevTo: prevTo.getTime(),
    chartFrom,
  };
}

// ── aggregation ────────────────────────────────────────────────────────

export function emptyTotals(): PeriodTotals {
  return { sales: 0, cogs: 0, grossProfit: 0, expenses: 0, fees: 0, netProfit: 0 };
}

export function sumMonths(months: DashboardMonth[], from: number, to: number): PeriodTotals {
  const startKey = monthKey(from);
  const endKey = monthKey(to);
  const totals = emptyTotals();
  for (const row of months) {
    if (row.month < startKey || row.month > endKey) continue;
    totals.sales += Number(row.sales) || 0;
    totals.cogs += Number(row.cogs) || 0;
    totals.expenses += Number(row.expenses) || 0;
    totals.fees += Number(row.fees) || 0;
  }
  totals.grossProfit = totals.sales - totals.cogs;
  totals.netProfit = totals.grossProfit - totals.expenses - totals.fees;
  return totals;
}

/** Add totals in place (used for portfolio aggregation). */
export function addTotals(target: PeriodTotals, add: PeriodTotals): PeriodTotals {
  target.sales += add.sales;
  target.cogs += add.cogs;
  target.grossProfit += add.grossProfit;
  target.expenses += add.expenses;
  target.fees += add.fees;
  target.netProfit += add.netProfit;
  return target;
}

export function sumAging(list: DashboardAgingSummary[]): DashboardAgingSummary {
  const out: DashboardAgingSummary = {
    total: 0, current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90p: 0, parties: 0,
  };
  for (const a of list) {
    out.total += Number(a.total) || 0;
    out.current += Number(a.current) || 0;
    out.d1_30 += Number(a.d1_30) || 0;
    out.d31_60 += Number(a.d31_60) || 0;
    out.d61_90 += Number(a.d61_90) || 0;
    out.d90p += Number(a.d90p) || 0;
    out.parties += Number(a.parties) || 0;
  }
  return out;
}

/** Combine stock-control totals; null when no outlet returned stock data. */
export function sumStock(list: Array<DashboardStockSummary | null | undefined>): DashboardStockSummary | null {
  const available = list.filter((s): s is DashboardStockSummary => !!s);
  if (!available.length) return null;
  const out: DashboardStockSummary = {
    skus: 0, outOfStock: 0, lowStock: 0, negativeStock: 0, slowMoving: 0, stockValue: 0,
  };
  for (const s of available) {
    out.skus += Number(s.skus) || 0;
    out.outOfStock += Number(s.outOfStock) || 0;
    out.lowStock += Number(s.lowStock) || 0;
    out.negativeStock += Number(s.negativeStock) || 0;
    out.slowMoving += Number(s.slowMoving) || 0;
    out.stockValue += Number(s.stockValue) || 0;
  }
  return out;
}

/** Merge the monthly rows of several outlets by month key. */
export function combineMonths(results: Array<Pick<DashboardResult, 'months'>>): DashboardMonth[] {
  const byMonth = new Map<string, DashboardMonth>();
  for (const result of results) {
    for (const row of result.months || []) {
      const key = row.month;
      const current = byMonth.get(key) || {
        month: key, sales: 0, cogs: 0, expenses: 0, fees: 0, grossProfit: 0, netProfit: 0,
      };
      current.sales += Number(row.sales) || 0;
      current.cogs += Number(row.cogs) || 0;
      current.expenses += Number(row.expenses) || 0;
      current.fees += Number(row.fees) || 0;
      current.grossProfit = current.sales - current.cogs;
      current.netProfit = current.grossProfit - current.expenses - current.fees;
      byMonth.set(key, current);
    }
  }
  return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
}

export function combineResults(results: DashboardResult[]): {
  months: DashboardMonth[];
  ar: DashboardAgingSummary;
  ap: DashboardAgingSummary;
  stock: DashboardStockSummary | null;
} {
  return {
    months: combineMonths(results),
    ar: sumAging(results.map((r) => r.ar)),
    ap: sumAging(results.map((r) => r.ap)),
    stock: sumStock(results.map((r) => r.stock)),
  };
}

/** Fill missing months with zeros so the chart keeps a stable x-axis. */
export function fillMonths(months: DashboardMonth[], from: number, to: number): DashboardMonth[] {
  const byMonth = new Map(months.map((m) => [m.month, m]));
  return monthKeysBetween(from, to).map((key) => byMonth.get(key) || {
    month: key, sales: 0, cogs: 0, expenses: 0, fees: 0, grossProfit: 0, netProfit: 0,
  });
}

/** Total for the immediately preceding period, or null when it was not loaded. */
export function previousTotals(months: DashboardMonth[], window: PeriodWindow): PeriodTotals | null {
  if (window.prevFrom < window.chartFrom) return null;
  return sumMonths(months, window.prevFrom, window.prevTo);
}

// ── health score ───────────────────────────────────────────────────────

export type HealthBand = 'healthy' | 'watch' | 'risk' | 'no-data';

export interface HealthComponent {
  key: string;
  label: string;
  score: number;
  weight: number;
  detail: string;
}

export interface HealthResult {
  score: number | null;
  band: HealthBand;
  label: string;
  reasons: string[];
  components: HealthComponent[];
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, Number.isFinite(n) ? n : 0));

/**
 * Rule-based 0–100 health score. The weights are intentionally simple and
 * documented; adjust them here rather than scattering thresholds in the UI.
 */
export function scoreHealth(
  totals: PeriodTotals,
  ar: DashboardAgingSummary,
  ap: DashboardAgingSummary,
  previous?: PeriodTotals | null,
): HealthResult {
  const activity = Math.abs(totals.sales) + Math.abs(totals.expenses) +
    Math.abs(ar.total) + Math.abs(ap.total);
  if (activity < 0.01) {
    return {
      score: null,
      band: 'no-data',
      label: 'No activity',
      reasons: ['No sales, expenses, receivables or payables in this period.'],
      components: [],
    };
  }

  const revenue = Math.max(0, totals.sales);
  const netMargin = revenue > 0 ? totals.netProfit / revenue : 0;
  const costRatio = revenue > 0 ? (totals.expenses + totals.fees) / revenue : 1;
  // "Overdue" in the health score means 31+ days. 1–30 days is still a
  // watch item in the ageing strip, but it has not breached the 30-day line.
  const overdue = Math.max(0, ar.d31_60 + ar.d61_90 + ar.d90p);
  const overdueRatio = ar.total > 0 ? overdue / ar.total : 0;
  const apPressure = revenue > 0 ? ap.total / revenue : (ap.total > 0 ? 1 : 0);

  const profitability = clamp01(netMargin / 0.20) * 100;
  const costControl = clamp01((1 - costRatio) / 0.50) * 100;
  const receivables = (1 - clamp01(overdueRatio / 0.50)) * 100;
  const payables = (1 - clamp01(apPressure)) * 100;

  const components: HealthComponent[] = [
    { key: 'profit', label: 'Profitability', score: profitability, weight: 35, detail: formatPct(netMargin) + ' net margin' },
    { key: 'cost', label: 'Cost control', score: costControl, weight: 20, detail: formatPct(costRatio) + ' of revenue spent' },
    { key: 'ar', label: 'Receivables', score: receivables, weight: 25, detail: formatPct(overdueRatio) + ' over 30 days' },
    { key: 'ap', label: 'Payables', score: payables, weight: 20, detail: formatPct(apPressure) + ' of period revenue' },
  ];

  let score = Math.round(components.reduce((s, c) => s + c.score * c.weight, 0) / 100);
  if (revenue <= 0) score = 0;
  score = Math.max(0, Math.min(100, score));

  const band: HealthBand = score >= 80 ? 'healthy' : score >= 60 ? 'watch' : 'risk';
  const label = band === 'healthy' ? 'Healthy' : band === 'watch' ? 'Watch' : 'At risk';

  const reasons: string[] = [];
  if (revenue <= 0) {
    reasons.push('No sales in this period.');
  } else if (totals.netProfit < 0) {
    reasons.push(`Net loss of ${formatMoney(Math.abs(totals.netProfit))} in this period.`);
  } else if (netMargin < 0.10) {
    reasons.push(`Net margin is ${formatPct(netMargin)} (target 10%+).`);
  }
  if (costRatio > 0.70) reasons.push(`Operating costs are ${formatPct(costRatio)} of revenue.`);
  if (overdueRatio > 0.20) reasons.push(`${formatPct(overdueRatio)} of receivables are 30+ days overdue.`);
  if (apPressure > 0.50) reasons.push(`Payables equal ${formatPct(apPressure)} of period revenue.`);
  if (previous && previous.sales > 0 && totals.sales < previous.sales * 0.80) {
    reasons.push(`Sales are down ${formatPct(1 - totals.sales / previous.sales)} vs the previous period.`);
  }
  if (!reasons.length) {
    if (band === 'healthy') {
      reasons.push('Profitable, costs under control and receivables current.');
    } else {
      const weakest = [...components].sort((a, b) => a.score - b.score).slice(0, 2);
      reasons.push(`Below target: ${weakest.map((c) => `${c.label} (${c.detail})`).join(', ')}.`);
    }
  }

  return { score, band, label, reasons: reasons.slice(0, 4), components };
}

// ── formatting ─────────────────────────────────────────────────────────

const moneyFmt = new Intl.NumberFormat('en-MY', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatMoney(n: number | undefined): string {
  return `RM ${moneyFmt.format(Number.isFinite(Number(n)) ? Number(n) : 0)}`;
}

export function formatCompactMoney(n: number | undefined): string {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0;
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${v < 0 ? '-' : ''}RM ${(abs / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `${v < 0 ? '-' : ''}RM ${(abs / 1_000).toFixed(1)}k`;
  return `${v < 0 ? '-' : ''}RM ${abs.toFixed(0)}`;
}

export function formatPct(n: number | undefined, digits = 1): string {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0;
  return `${(v * 100).toFixed(digits)}%`;
}

export interface DeltaValue {
  text: string;
  direction: 'up' | 'down' | 'flat';
}

/** Percentage change vs the previous period; null when there is no baseline. */
export function formatDelta(current: number, previous: number | null | undefined): DeltaValue | null {
  if (previous == null || !Number.isFinite(previous)) return null;
  if (Math.abs(previous) < 0.005) {
    return current > 0 ? { text: 'new', direction: 'up' } : { text: '0.0%', direction: 'flat' };
  }
  const change = (current - previous) / Math.abs(previous);
  if (Math.abs(change) < 0.0005) return { text: '0.0%', direction: 'flat' };
  return {
    text: `${change > 0 ? '+' : ''}${(change * 100).toFixed(1)}%`,
    direction: change > 0 ? 'up' : 'down',
  };
}

// ── loading ────────────────────────────────────────────────────────────

/** Load one outlet's dashboard snapshot for a resolved period. */
export function loadOutletDashboard(storeCode: string, window: PeriodWindow): Promise<DashboardResult> {
  return api.accountingDashboard(storeCode, {
    from: window.from,
    to: window.to,
    chartFrom: window.chartFrom,
    asOf: Date.now(),
  });
}

export function accessibleDashboardStores(stores: DashboardStoreInput[]): DashboardStoreInput[] {
  return stores.filter((s) => !s.renewalOnly && (s.accessAs || []).includes('ET_Dashboard'));
}

/** Run at most `limit` async jobs at once, preserving input order. */
export async function mapLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    for (;;) {
      const index = next++;
      if (index >= items.length) return;
      out[index] = await fn(items[index], index);
    }
  });
  await Promise.all(workers);
  return out;
}
